import { action, makeObservable, observable, toJS } from "mobx"
import { UploadFile } from "antd"
import { BannerForm } from "./entities/BannerForm"

const SECONDARY_BANNERS_AMOUNT = 4
interface Props {
	microsite: string
	banners: BannerForm[]
}

export class BannersFormViewModel {
	selectedLanguage = "ES"

	microsite: string
	banners: BannerForm[] = []

	constructor({ microsite, banners }: Props) {
		makeObservable(this, {
			selectedLanguage: observable,
			banners: observable,
			microsite: observable,
			setSelectedLanguage: action,
			setBanners: action,
			addBanner: action,
			removeBanner: action,
			reorderBanners: action,
			setBannerHref: action,
			loadNewBannerImage: action,
			removeNewBannerImage: action,
			setMicrosite: action,
			toggleBannerEnabled: action
		})
		this.microsite = microsite
		this.banners = banners
	}

	setBanners(banners: BannerForm[]) {
		this.banners = banners
		const secondaryBanners = this.getBanners("secondaryBanner", true)
		if (secondaryBanners.length < SECONDARY_BANNERS_AMOUNT) {
			// If secondary banner is missing, add missing one with corresponding subType
			const subTypes = ["large", "large", "short", "short"]
			const usedIndices = new Set<number>()
			const newSecondaryBanners = subTypes.map((subType, idx) => {
				// Find an existing banner of this subtype and not already used
				const existingIdx = secondaryBanners.findIndex((b, i) => b.subType === subType && !usedIndices.has(i))
				if (existingIdx !== -1) {
					usedIndices.add(existingIdx)
					const banner = secondaryBanners[existingIdx]
					return new BannerForm(
						{
							...banner,
							weight: idx + 1,
							subType
						},
						banner.viewId,
						true
					)
				} else {
					return new BannerForm(
						{
							key: this.microsite,
							weight: idx + 1,
							type: "secondaryBanner",
							subType,
							language: this.selectedLanguage
						},
						undefined,
						false,
						true
					)
				}
			})
			const otherBanners = this.banners.filter(
				b => b.language.toLowerCase() !== this.selectedLanguage.toLowerCase() || b.type !== "secondaryBanner"
			)
			this.banners = [...otherBanners, ...newSecondaryBanners]
		}
	}

	setSelectedLanguage(language: string) {
		this.selectedLanguage = language
	}

	addBanner(banner: BannerForm) {
		this.addBanners([banner])
	}

	addBanners(banners: BannerForm[]) {
		banners.forEach(b => b.setChanged(true))
		this.banners = this.banners.concat(banners)
	}

	removeBanner(banner: BannerForm) {
		if (banner.desktopImage || banner.mobileImage) {
			this.getBanner(banner.viewId)?.delete()
		} else {
			// Remove from list if no uploaded images
			this.banners = this.banners.filter(b => b.viewId !== banner.viewId)
		}
		this.reorderBanners(banner.type, 0, 0)
	}

	getBanner(viewId: string) {
		return this.banners.find(b => b.viewId === viewId) ?? null
	}

	getBanners(type: "mainBanner" | "secondaryBanner", withDeleted = false) {
		return this.banners.filter(
			b =>
				(!b.deleted || withDeleted) &&
				b.type === type &&
				b.language.toLowerCase() === this.selectedLanguage.toLowerCase()
		)
	}

	reorderBanners(type: "mainBanner" | "secondaryBanner", startIndex: number, destinationIndex: number) {
		const banners = Array.from(this.getBanners(type))
		const [removed] = banners.splice(startIndex, 1)
		banners.splice(destinationIndex, 0, removed)
		const otherBanners = this.banners.filter(
			b => b.language.toLowerCase() !== this.selectedLanguage.toLowerCase() || b.type !== type || b.deleted
		)
		const orderedBanners = banners
			.map((b, idx) => new BannerForm({ ...b, weight: idx + 1 }, b.viewId, true))
			.concat(otherBanners)
		this.banners = orderedBanners
	}

	setBannerHref(viewId: string, href: string) {
		this.getBanner(viewId)?.setHref(href)
	}

	loadNewBannerImage(viewId: string, newImage: UploadFile, imageType: "desktop" | "mobile") {
		imageType === "desktop"
			? this.getBanner(viewId)?.setDesktopImage(newImage)
			: this.getBanner(viewId)?.setMobileImage(newImage)
		// Update reference to refresh view
		this.banners = [...this.banners]
	}

	removeNewBannerImage(viewId: string, imageType: "desktop" | "mobile") {
		imageType === "desktop"
			? this.getBanner(viewId)?.setDesktopImage(undefined)
			: this.getBanner(viewId)?.setMobileImage(undefined)
		// Update reference to refresh view
		this.banners = [...this.banners]
	}

	setMicrosite(microsite: string) {
		this.microsite = microsite
	}

	/**
	 * Toggle the enabled/disabled (deleted) state of a secondary banner by viewId.
	 */
	toggleBannerEnabled(viewId: string, enabled: boolean) {
		const banner = this.getBanner(viewId)
		if (!banner) return
		if (enabled) {
			banner.undoDelete()
		} else {
			banner.delete()
		}
		// Update reference to refresh view
		this.banners = [...this.banners]
	}
}
